import apiBaseState from "~/constants/apiBaseState";
import notifyErrorDefaults from "~/constants/notifyErrorDefaults";
import {getDistributionsBaseUrl} from "~/plugins/Api/distributionsApi";

const getBatchInitialState = () => {
    return {
        currentBatch: apiBaseState(),
        currentBatchQuery: {
            page: 1,
            length: 10,
        },
        previousBatches: apiBaseState(),
        pendingBatches: apiBaseState()
    };
};

const getInitialDistributionsQuery = () => ({
    page: 1,
    length: 10,
    column: 'created_at',
    direction: 'desc'
});

const getInitialDistributionsAnalysisQuery = () => ({
    page: 1,
    length: 10,
    direction: 'desc'
});

export const state = () => ({
    distributions: null,
    distributionsQuery: getInitialDistributionsQuery(),
    currentDistributionId: null,
    batches: getBatchInitialState(),
    distributionAnalysis: apiBaseState(),
    distributionAnalysisQuery: getInitialDistributionsAnalysisQuery(),
    slugs: null,
    clearCurrentBatchState: apiBaseState(),
    createMailoutTemplateState: apiBaseState(),
    updateMailoutTemplateState: apiBaseState(),
    deleteMailoutTemplateState: apiBaseState(),
    getMailoutTemplateState: apiBaseState(),
    getAllMailoutTemplatesState: apiBaseState(),
});

export const getters = {
    isCurrentBatchValid: (state) => {
        const response = {
            valid: false,
            errors: [],
            hasUsers: true
        };

        if(!state.distributions?.data){
            return response;
        }
        const distribution = state.distributions.data.find(distribution =>
            parseInt(distribution.id) === parseInt(state.currentDistributionId));
        if(!distribution){
            return response;
        }
        const hasUsers = state.batches.currentBatch.data?.data?.length > 0;
        const hasContent = distribution.top_content;

        if(!hasUsers){
            response.hasUsers = false;
            response.errors.push('Please add users to this mailout');
        }

        if(!hasContent){
            response.errors.push('Please add content to the mailout');
        }
        response.valid = hasContent && hasUsers;
        return response;
    },
    slugs: (state) => {
        return {
            accountSlug: state.slugs?.accountSlug,
            projectSlug: state.slugs?.projectSlug,
            taskSlug: state.slugs?.taskSlug,
        };
    },
    slugsReady: (state) => {
        return state.slugs !== null;
    },
    hasRecipients: (state) => {
        return state.batches.currentBatch.data?.data?.length > 0;
    },
    hasRecipientSearchOccurred: (state) => {
        return state.batches.currentBatch.data
            && state.batches.currentBatch.data.meta;
    },
    noPreviousBatches: (state) => {
        return state.batches.previousBatches.data && state.batches.previousBatches.data.length === 0;
    }
};

export const mutations = {
    SET_SLUGS(state, data) {
        state.slugs = data;
    },
    RESET_DISTRIBUTIONS(state) {
        state.distributions = null;
        state.distributionsQuery = getInitialDistributionsQuery();
        state.currentDistributionId = null;
        state.batches = getBatchInitialState();
        state.distributionAnalysis = apiBaseState();
        state.distributionAnalysisQuery = getInitialDistributionsAnalysisQuery();
    },
    SET_DISTRIBUTIONS(state, data) {
        state.distributions = data;
    },
    ADD_DISTRIBUTION(state, data) {
        state.distributions.data.unshift(data);
    },
    DELETE_DISTRIBUTION(state, data) {
        const {id} = data;
        state.distributions.data = state.distributions.data.filter(distribution => distribution.id !== id);
    },
    UPDATE_DISTRIBUTION(state, payload) {
        const {data, distributionId} = payload;
        const index = state.distributions.data.findIndex(distribution => distribution.id === parseInt(distributionId));
        state.distributions.data.splice(index, 1, data);
    },
    SET_DISTRIBUTIONS_QUERY(state, data) {
        state.distributionsQuery = data;
    },
    SET_CURRENT_BATCH(state, payload) {
        state.batches.currentBatch = {...state.batches.currentBatch, ...payload};
    },
    SET_CURRENT_BATCH_QUERY(state, payload) {
        state.batches.currentBatchQuery = payload;
    },
    SET_PREVIOUS_BATCHES(state, data) {
        state.batches.previousBatches = data;
    },
    CLEAR_RECIPIENTS(state, data) {
        state.batches = getBatchInitialState();
    },
    UPDATE_RECIPIENT_ACTIVE_STATUS(state, data){
        const { id } = data;
        const index = state.batches.currentBatch.data.data
            .findIndex(recipient => recipient.id === id);

        state.batches.currentBatch.data.data.splice(index, 1);

        state.batches.currentBatch.data.batch.pending_users_count -= 1;

    },
    SET_PENDING_BATCHES(state, data) {
        state.batches.pendingBatches = data;
    },
    ADD_PENDING_BATCH(state, data) {
        state.batches.pendingBatches.data.push(data);
    },
    REMOVE_PENDING_BATCH(state, {id}) {
        state.batches.pendingBatches.data = state.batches.pendingBatches.data.filter(batch => batch.id !== id);
    },
    CLEAR_CURRENT_BATCH(state){
        state.batches.currentBatch = apiBaseState();
    },
    SET_DISTRIBUTION_ID(state, data){
        state.currentDistributionId = data;
    },
    SET_DISTRIBUTION_ANALYSIS(state, data){
        state.distributionAnalysis = data;
    },
    SET_ANALYSIS_QUERY(state, data){
        state.distributionAnalysisQuery = data;
    },
    CLEAR_CURRENT_BATCH_STATE(state, data) {
        state.clearCurrentBatchState = data;
    },
    SET_CREATE_MAILOUT_TEMPLATE_STATE(state, data) {
        state.createMailoutTemplateState = data;
    },
    SET_UPDATE_MAILOUT_TEMPLATE_STATE(state, data) {
        state.updateMailoutTemplateState = data;
    },
    SET_DELETE_MAILOUT_TEMPLATE_STATE(state, data) {
        state.deleteMailoutTemplateState = data;
    },
    SET_GET_MAILOUT_TEMPLATE_STATE(state, data) {
        state.getMailoutTemplateState = data;
    },
    SET_GET_ALL_MAILOUT_TEMPLATES_STATE(state, data) {
        state.getAllMailoutTemplatesState = data;
    },
    DELETE_MAILOUT_ITEM_BY_ID(state, id) {
        const matchedIndex = state.getAllMailoutTemplatesState.data.findIndex(item => item.id === id);
        if(matchedIndex > -1){
            state.getAllMailoutTemplatesState.data.splice(matchedIndex, 1);
        }
    },
    UPDATE_MAILOUT_ITEM_BY_ID(state, template) {
        const matchedIndex = state.getAllMailoutTemplatesState.data.findIndex(item => item.id === template.id);
        if(matchedIndex > -1){
            state.getAllMailoutTemplatesState.data.splice(matchedIndex, 1, template);
        }
    },
};

export const actions = {
    async GET_PROFILE_GROUPS({commit}, data) {
        const {path, $notify} = data;
        try {
            const response = await this.$api.fetchProfileGroupCategories(path);
            commit("SET_PROFILE_GROUPS", response.data);
        } catch (e) {
            console.log(e);
            $notify({
                ...notifyErrorDefaults,
                text: 'Error loading questions for quota'
            });
        }
    },
    GET_USERS_FOR_DISTRIBUTION(state, payload) {
        const {params, distributionId, accountSlug, projectSlug, taskSlug} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug });
        const url = `${baseUrl}/distributions/${distributionId}/users`;
        return this.$whyApi.get({
            url,
            params,
            mutation: "distributions/SET_CURRENT_BATCH",
            errorMessage: "Error getting users"
        });
    },
    GET_ANALYSIS_FOR_DISTRIBUTION(state, payload) {
        const {distributionId, isRefinementSearch, accountSlug, projectSlug, taskSlug, params} = payload;

        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug });
        const url = `${baseUrl}/distributions/${distributionId}/analysis`;
        return this.$whyApi.get({
            url,
            params,
            isRefinementSearch,
            mutation: "distributions/SET_DISTRIBUTION_ANALYSIS",
            errorMessage: "Error getting analysis"
        });
    },
    GET_CURRENT_BATCH(state, payload) {
        const {distributionId, params, accountSlug, projectSlug, taskSlug, ...data} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug });
        const url = `${baseUrl}/distributions/${distributionId}/current-batch`;
        return this.$whyApi.get({
            url,
            params,
            mutation: "distributions/SET_CURRENT_BATCH",
            errorMessage: "Error getting batch",
            ...data
        });
    },
    GET_PREVIOUS_BATCHES(state, payload) {
        const {distributionId, accountSlug, projectSlug, taskSlug} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug });
        const url = `${baseUrl}/distributions/${distributionId}/previous-batches`;
        return this.$whyApi.get({
            url,
            mutation: "distributions/SET_PREVIOUS_BATCHES",
            errorMessage: "Error getting batches",
        });
    },
    GET_PENDING_BATCHES(state, payload) {
        const {distributionId, accountSlug, projectSlug, taskSlug} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug });
        const url = `${baseUrl}/distributions/${distributionId}/pending-batches`;
        return this.$whyApi.get({
            url,
            mutation: "distributions/SET_PENDING_BATCHES",
            errorMessage: "Error getting pending batches",
        });
    },
    CLEAR_CURRENT_BATCH_STATE(state, payload){
        const {distributionId, accountSlug, projectSlug, taskSlug} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug });
        const url = `${baseUrl}/distributions/${distributionId}/current-batch/clear`;
        return this.$whyApi.delete({
            url,
            mutation: "distributions/CLEAR_CURRENT_BATCH_STATE",
            errorMessage: "Error clearing current batch",
        });
    },
    CREATE_MAILOUT_TEMPLATE({ commit }, payload) {
        const {distributionId, accountSlug, projectSlug, ...updatePayload} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug: undefined });

        const url = `${baseUrl}/distributions/${distributionId}/save-as-template`;
        return this.$whyApi.post({
            url,
            data: updatePayload,
            mutation: "distributions/SET_CREATE_MAILOUT_TEMPLATE_STATE",
            errorMessage: "Error creating mailout template",
        });
    },
    async UPDATE_MAILOUT_TEMPLATE({ commit }, payload) {
        const {templateId, accountSlug, projectSlug, updatePayload} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug: undefined });
        const url = `${baseUrl}/distributions/templates/${templateId}`;
        const response = await this.$whyApi.post({
            url,
            data: updatePayload,
            handleMedia: true,
            mutation: "distributions/SET_UPDATE_MAILOUT_TEMPLATE_STATE",
            errorMessage: "Error updating mailout template",
        });
        commit('UPDATE_MAILOUT_ITEM_BY_ID', response.data);
        return response;
    },
    async DELETE_MAILOUT_TEMPLATE({ commit }, payload){
        const {templateId, accountSlug, projectSlug} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug: undefined });
        const url = `${baseUrl}/distributions/templates/${templateId}`;
        const response = await this.$whyApi.delete({
            url,
            mutation: "distributions/SET_DELETE_MAILOUT_TEMPLATE_STATE",
            errorMessage: "Error deleting mailout template",
        });
        commit('DELETE_MAILOUT_ITEM_BY_ID', templateId);
        return response;
    },
    GET_MAILOUT_TEMPLATE({ commit }, payload){
        const {templateId, accountSlug, projectSlug} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug: undefined });
        const url = `${baseUrl}/distributions/templates/${templateId}`;
        return this.$whyApi.get({
            url,
            mutation: "distributions/SET_GET_MAILOUT_TEMPLATE_STATE",
            errorMessage: "Error getting mailout template",
        });
    },
    GET_ALL_MAILOUT_TEMPLATES({ commit }, payload){
        const {accountSlug, projectSlug} = payload;
        const baseUrl = getDistributionsBaseUrl({accountSlug, projectSlug, taskSlug: undefined });
        const url = `${baseUrl}/distributions/templates`;
        return this.$whyApi.get({
            url,
            mutation: "distributions/SET_GET_ALL_MAILOUT_TEMPLATES_STATE",
            errorMessage: "Error getting mailout templates",
        });
    }
};
